<template lang="pug">
  WtTile#teams-generator(ancestor vertical :col="12")
    WtTile(parent :col="12")
      WtTile(child child-class="" :col="12")
        .level.teams-generator-header
          .level-left
            .level-item
              WtTitle.team-generator-title(h2) Create Multiple Teams
          .level-right
            .level-item
              WtButton(is-router-link :to="{ name: 'TeamCenterSettings' }" color="primary") BACK TO TEAM CENTER SETTINGS
    BLoading(
      :is-full-page="false"
      :active.sync="pageLoading"
    )
    WtTile(parent :col="12" vertical)
      WtTile(child :col="12")
        WtTile(ancestor)
          WtTile(parent :col="6" vertical)
            WtTitle(card-title) Enter a list of team names
              span.has-text-weight-light.pl-1 (one name per line)
            WtTile(child :col="12").has-background-light
              #enter-team-name-list.animated.fadeIn
                BInput#team-name-list-input(:rows="12" type="textarea" v-model="teamNameList")
            .is-flex.is-justify-content-center
              BField
                WtButton(
                  size="medium"
                  bold
                  :disabled="!teamNameList"
                  @click.native="generateTeamNames"
                ) Generate Teams
          WtTile(parent :col="6" vertical)
            WtTitle(card-title) Generated Teams
            WtTile(child :col="12").has-background-light
              #generated-teams-table
                BTable(
                  :key="'generatedTeamsForceUpdateKey'+generatedTeamsForceUpdateKey"
                  ref="table"
                  hoverable
                  :data="generatedTeamNames"
                  :paginated="false"
                  :show-header="false"
                  :scrollable="true"
                  :height="312"
                  custom-row-key="originalValue"
                )
                  BTableColumn(field="name" label="")
                    template(v-slot:default="props")
                      UniquenessCheck(
                        @click.native.stop=""
                        readonly
                        :required="false"
                        entity="teamName"
                        :placeholder="props.row.name"
                        v-model="props.row.name"
                        :original-value="props.row.name"
                        show-cancel
                        @cancel="props.row.name = $event"
                        triggerUniquenessCheckOnMount
                        @validName="setValidTeam(props.row, $event)"
                        @loading="$set(uniquenessCheckLoading, 'team-'+props.row.id, $event)"
                      )
            .is-flex.is-justify-content-center
              BField
                WtButton(
                  size="medium"
                  bold
                  :disabled="disableSaveTeams"
                  :loading="savingTeams"
                  @click.native="saveTeams"
                ) Save Teams
</template>

<script>
import { mapGetters } from "vuex";
import WtTile from "@@/WtTile/WtTile";
import WtTitle from "@@/WtTitle/WtTitle";
import WtButton from "@@/WtButton/WtButton";
import TeamList from "@@/TeamCenterAdmin/TeamList";
import UniquenessCheck from "@@/WtUniquenessCheckInput/WtUniquenessCheckInput";
import MembersTable from "@@/Members/MembersTable";

const teamStub = {
  id: 0,
  name: "",
  public: true,
  numMembers: 0,
  teamMembers: []
};

export default {
  name: "CreateMultipleTeams",

  components: {
    WtButton,
    WtTile,
    WtTitle,
    TeamList,
    UniquenessCheck,
    MembersTable
  },

  data() {
    return {
      pageLoading: false,
      allProgramTeams: [],
      teamNameList: "",
      generatedTeamNames: [],
      validTeams: {},
      uniquenessCheckLoading: {},
      savingTeams: false,
      generatedTeamsForceUpdateKey: 0
    };
  },

  computed: {
    ...mapGetters("program", ["coreProgramMeta"]),

    teamNameListArray() {
      return this.teamNameList
        .split("\n")
        .map(teamName => teamName.trim())
        .filter(teamName => teamName.length > 0);
    },

    disableSaveTeams() {
      const noTeamsGenerated = this.generatedTeamNames.length < 1;
      const uniquenessCheckLoading =
        Object.values(this.uniquenessCheckLoading).filter(loading => loading)
          .length > 0;
      const teamNameError =
        Object.values(this.validTeams).filter(team => !team.valid).length > 0;

      return noTeamsGenerated || uniquenessCheckLoading || teamNameError;
    }
  },

  beforeRouteLeave(_, __, next) {
    this.checkForNotSavedWork(next);
  },

  methods: {
    setValidTeam(team, event) {
      this.$set(this.validTeams, `team-${team.id}`, {
        name: team.name,
        valid: event
      });
    },

    generateTeamNames() {
      this.validTeams = {}; // if the UniquenessCheck isn't re-mounted this can causes duplicates to be accepted
      const uniqueTeamNames = [...new Set(this.teamNameListArray)];
      let teams = uniqueTeamNames.map((teamName, index) => {
        return {
          id: index,
          originalValue: teamName,
          name: teamName
        };
      });
      this.generatedTeamNames = teams;
      this.$buefy.toast.open(`Teams Preview Generated!`);
      this.generatedTeamsForceUpdateKey++; // forces the table/UniquenessCheck to re-mount, otherwise duplicates bug because of validTeams & originalName
      return teams;
    },

    generateFinalTeams() {
      return Object.entries(this.validTeams).map(([key, value]) => {
        let team = this._.cloneDeep(teamStub);
        team.id = key;
        team.name = value.name;
        return team;
      });
    },

    async saveTeams() {
      const validTeamNames = Object.values(this.validTeams).map(team =>
        team.name.toLowerCase()
      );
      const duplicates = validTeamNames.filter(
        (item, index) => validTeamNames.indexOf(item) !== index
      );
      if (duplicates.length > 0) {
        this.$buefy.toast.open({
          type: "is-danger",
          duration: 6000,
          message:
            "It looks like you have at least one duplicate team name. Please correct it and then try again."
        });
        return;
      }

      this.teamNameList = "";
      this.savingTeams = true;
      this.pageLoading = true;
      const payload = {
        maxTeamSize: this.coreProgramMeta.maxTeamSize,
        teams: this.generateFinalTeams(),
        includeByType: "invite"
      };

      const response = await this.axios.post("teams/generator", payload);
      if (response.data.success) {
        await this.$store.dispatch(
          "snackBar",
          {
            duration: 10000,
            queue: false,
            message: "Teams successfully created!",
            type: "is-success"
          },
          { root: true }
        );
      }

      this.generatedTeamNames = [];
      this.savingTeams = false;
      this.pageLoading = false;
    },

    checkForNotSavedWork(next) {
      if (this.generatedTeamNames.length > 0) {
        this.$buefy.dialog.confirm({
          title: "Teams Generated Have Not Been Saved",
          confirmText: "Thanks, I do not need to save",
          type: "is-danger",
          hasIcon: true,
          message: `Are you sure you want to abandon your work on generated teams? Work will be lost.`,
          onConfirm: () => {
            next();
          },
          onCancel: () => {
            next(false);
          }
        });
      } else {
        next();
      }
    }
  }
};
</script>

<style lang="sass">
#generated-teams-table
  min-height: 312px
  background: $white
  border-radius: $radius-large
  .b-table
    .table-container
      overflow-y: auto
</style>
